import { Users } from "lucide-react";
import React, { useContext, useMemo } from "react";
import {
  Text,
  Space,
  Divider,
  Group,
  Grid,
  Button,
  Table,
  Alert,
  Loader,
  Breadcrumbs,
  Anchor,
  useMantineTheme,
} from "@mantine/core";
import { AuthContext } from "../../../providers/AuthProvider";
import Segment from "../services/model/Segment";
import SegmentStatusBadge from "../elements/SegmentStatusBadge";
import useAvailableFieldsApiService from "../services/api/AvailableFieldsApiService";
import useSegmentPreviewApiService from "../services/api/SegmentPreviewApiService";
import {
  getOperatorLabel,
  getDateOperatorLabel,
  SEGMENT_PREVIEW_COLORS,
  RELATIVE_DATE_PATTERN,
  formatRelativeDateDisplay,
} from "../constants/segmentConstants";
import { areRulesValid } from "../utils/ruleValidation";
import { formatDateTime } from "../../../utils/DateTime";

type SegmentViewContentProps = {
  segment: Segment;
  onBack: () => void;
};

const SegmentViewContent: React.FC<SegmentViewContentProps> = ({
  segment,
  onBack,
}) => {
  const auth = useContext(AuthContext);
  const theme = useMantineTheme();

  const { data: availableFields } = useAvailableFieldsApiService(
    { employer: auth?.employer.id ?? 0 },
    Boolean(auth?.employer?.id)
  );

  const fieldLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (availableFields) {
      availableFields.forEach((field) => {
        map[field.field] = field.label;
      });
    }
    return map;
  }, [availableFields]);

  const previewRequest = useMemo(
    () => ({
      employer: auth?.employer.id ?? 0,
      rules: segment.rules || [],
      logic_operator: segment.logic_operator || "OR",
    }),
    [auth?.employer.id, segment.rules, segment.logic_operator]
  );

  const isValidRules = useMemo(
    () => areRulesValid(segment.rules || []),
    [segment.rules]
  );

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useSegmentPreviewApiService(previewRequest, isValidRules);

  const formatValue = (
    value: string | number | boolean | undefined,
    operator?: string
  ): string => {
    if (value === undefined || value === null || value === "") {
      return "-";
    }
    if (typeof value === "boolean") {
      return value ? "True" : "False";
    }
    // Check if value is a relative date (e.g., "30d", "2w", "3m", "1y")
    const stringValue = String(value);
    if (RELATIVE_DATE_PATTERN.test(stringValue)) {
      // Pass the operator so bounded windows (within_next/within_last) omit "ago".
      return formatRelativeDateDisplay(stringValue, operator);
    }
    return stringValue;
  };

  const INACTIVE_STATUSES = ["expired", "closed"];

  const activeAnnouncements = (segment.usages?.announcements ?? []).filter(
    (a) => !INACTIVE_STATUSES.includes(a.status)
  );
  const activeForms = (segment.usages?.forms ?? []).filter(
    (f) => !INACTIVE_STATUSES.includes(f.status)
  );
  const activeEwaBlocking = (segment.usages?.ewa_blocking ?? []).filter(
    (e) => e.is_active
  );

  const isInUse = segment.usages
    ? activeAnnouncements.length > 0 ||
      activeForms.length > 0 ||
      activeEwaBlocking.length > 0
    : segment.assignment_count > 0;

  const buildUsageText = (): string => {
    if (!segment.usages) {
      return isInUse
        ? "This segment is currently active and in use."
        : "This segment is not currently in use.";
    }
    const parts: string[] = [];
    if (activeAnnouncements.length > 0) {
      const n = activeAnnouncements.length;
      const titles = activeAnnouncements.map((a) => a.title).join(", ");
      parts.push(`${n} announcement${n > 1 ? "s" : ""}: ${titles}`);
    }
    if (activeForms.length > 0) {
      const n = activeForms.length;
      const titles = activeForms.map((f) => f.title).join(", ");
      parts.push(`${n} survey${n > 1 ? "s" : ""}: ${titles}`);
    }
    if (activeEwaBlocking.length > 0) {
      parts.push("an EWA blocking rule");
    }
    if (parts.length === 0) return "This segment is not currently in use.";
    const joined =
      parts.length === 1
        ? parts[0]
        : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
    return `This segment is currently active and used in ${joined}.`;
  };

  const renderPreviewAlert = (): JSX.Element => {
    if (isPreviewLoading) {
      return (
        <Alert
          icon={<Loader size={16} />}
          color="orange"
          variant="light"
          styles={{
            root: {
              backgroundColor: SEGMENT_PREVIEW_COLORS.background,
            },
          }}
        >
          <Text size="sm" weight={500} color={SEGMENT_PREVIEW_COLORS.text}>
            Calculating estimated match...
          </Text>
        </Alert>
      );
    }

    if (isPreviewError) {
      return (
        <Alert
          icon={<Users size={16} />}
          color="red"
          variant="light"
          styles={{
            root: {
              backgroundColor: "rgba(255, 107, 107, 0.1)",
            },
          }}
        >
          <Text size="sm" color="red">
            Unable to calculate estimated match
          </Text>
        </Alert>
      );
    }

    return (
      <Alert
        icon={<Users size={16} />}
        color="orange"
        variant="light"
        styles={{
          root: {
            backgroundColor: SEGMENT_PREVIEW_COLORS.background,
          },
        }}
      >
        <Text size="sm" weight={500} color={SEGMENT_PREVIEW_COLORS.text}>
          Estimated match:{" "}
          {previewData?.matched_count ?? segment.matched_employee_count ?? 0}{" "}
          Employees
        </Text>
      </Alert>
    );
  };

  const breadcrumbItems = [
    { id: "segmentation", title: "Segmentation", onClick: onBack },
    { id: "segment-name", title: segment.name, onClick: undefined },
  ].map((item, index, arr) => (
    <Anchor
      key={item.id}
      onClick={(e) => {
        e.preventDefault();
        if (item.onClick) {
          item.onClick();
        }
      }}
      size="sm"
      style={{
        color: theme.black,
        opacity: index === arr.length - 1 ? 1 : 0.6,
        cursor: index === arr.length - 1 ? "default" : "pointer",
        textDecoration: "none",
      }}
    >
      {item.title}
    </Anchor>
  ));

  return (
    <>
      <Space h="md" />
      <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
      <Space h="xl" />

      <Text weight={600} size="lg" mb="md">
        Segment details
      </Text>

      <Text weight={500} size="sm" mb={4}>
        Segment name
      </Text>
      <Text size="sm" color="dimmed" mb="md">
        {segment.name}
      </Text>

      <Text weight={500} size="sm" mb={4}>
        Description
      </Text>
      <Text size="sm" color="dimmed" mb="md">
        {segment.description}
      </Text>

      <Space h="md" />
      <Divider />
      <Space h="xl" />

      <Text weight={600} size="md" mb="md">
        Rules criteria
      </Text>

      <Table horizontalSpacing="sm" verticalSpacing="xs" fontSize="sm">
        <thead>
          <tr>
            <th style={{ width: "33%" }}>Field</th>
            <th style={{ width: "33%" }}>Rule</th>
            <th style={{ width: "34%" }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {(segment.rules || []).map((rule, index) => {
            const isRelativeDate =
              typeof rule.value === "string" &&
              RELATIVE_DATE_PATTERN.test(rule.value);
            const operatorLabel = isRelativeDate
              ? getDateOperatorLabel(rule.operator)
              : getOperatorLabel(rule.operator);

            return (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={`rule-${rule.field}-${index}`}>
                {index > 0 && (
                  <tr>
                    <td colSpan={3}>
                      <Text size="xs" weight={700} color="dark" py={4}>
                        {segment.logic_operator}
                      </Text>
                    </td>
                  </tr>
                )}
                <tr>
                  <td>
                    <Text size="sm" color="dimmed">
                      {fieldLabelMap[rule.field] || rule.field}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed">
                      {operatorLabel}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed">
                      {formatValue(rule.value, rule.operator)}
                    </Text>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      <Space h="xl" />
      <Divider />
      <Space h="xl" />

      <Text weight={500} size="sm" mb={4}>
        Date created
      </Text>
      <Text size="sm" color="dimmed" mb="md">
        {formatDateTime(segment.created_at)}
      </Text>

      <Text weight={500} size="sm" mb={4}>
        Created by (User)
      </Text>
      <Text size="sm" color="dimmed" mb="md">
        {segment.created_by.first_name} {segment.created_by.last_name}
      </Text>

      <Text weight={500} size="sm" mb={4}>
        Segment status
      </Text>
      <Grid mb="xl">
        <Grid.Col span={6}>
          <Text size="sm" color="dimmed">
            {buildUsageText()}
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <SegmentStatusBadge isActive={isInUse} />
        </Grid.Col>
      </Grid>

      {renderPreviewAlert()}

      <Space h="xl" />

      <Group position="right">
        <Button
          variant="outline"
          radius={8}
          onClick={onBack}
          styles={{
            root: {
              borderColor: "#062133",
              color: "#062133",
              fontWeight: 600,
            },
          }}
        >
          Close
        </Button>
      </Group>
    </>
  );
};

export default SegmentViewContent;
